import React, { useState } from 'react';
import {
  DataObject as DataObjectIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import type { MatchedPatientData } from '@store/generated/admin';

import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import {hasMatches, normalizePatientMatches } from '../types/patient-matching';

import {
  ActionButtonsSection,
  ExtractedPatientSection,
  SourceSelectionSection,
  TargetSelectionSection,
} from './enhanced-patient-match-components';
import { PatientDataJsonModal } from './patient-data-json-modal';

interface PatientMatchReviewProps {
  patient: MatchedPatientData;
  isSelected?: boolean;
  isProcessed?: boolean;
  onToggleSelection?: (patientId: string) => void;
  onMergeDecision?: (patientId: string, decision: 'merge' | 'import_as_new', mergeTargetId?: string, sourcePIds?: string[]) => void;
  isLoading?: boolean;
}

export const PatientMatchReview: React.FC<PatientMatchReviewProps> = ({
  patient,
  isSelected = false,
  isProcessed = false,
  onToggleSelection,
  onMergeDecision,
  isLoading = false,
}) => {
  const matches = normalizePatientMatches(patient);
  const hasPatientMatches = hasMatches(patient);
  const { confirm, ConfirmationDialog } = useConfirmationDialog();
  const [jsonModalOpen, setJsonModalOpen] = useState(false);

  // Enhanced merge state
  const [targetPatientId, setTargetPatientId] = useState<string | null>(
    matches.length > 0 ? matches[0].patient_id : null // Default to best match
  );
  const [mergeExtracted, setMergeExtracted] = useState(true);
  const [mergeExistingIds, setMergeExistingIds] = useState<Set<string>>(new Set());

  const handleMerge = () => {
    if (!targetPatientId || (!mergeExtracted && mergeExistingIds.size === 0)) {
      return;
    }

    // Combine all source IDs for the API call
    const allSourceIds: string[] = [];
    if (mergeExtracted) {
      allSourceIds.push(patient.extracted_patient_id);
    }
    allSourceIds.push(...Array.from(mergeExistingIds));

    onMergeDecision?.(patient.extracted_patient_id, 'merge', targetPatientId, allSourceIds);
  };

  const handleImportAsNew = async () => {
    // Check for high-confidence matches (90%+) and show confirmation
    const highConfidenceMatches = matches.filter(match => match.confidence >= 90);

    if (highConfidenceMatches.length > 0) {
      const bestMatch = highConfidenceMatches[0];
      const patientName = `${bestMatch.patient_data?.first_name || ''} ${bestMatch.patient_data?.last_name || ''}`.trim();
      const patientDetails = [
        bestMatch.patient_data?.date_of_birth && `DOB: ${bestMatch.patient_data.date_of_birth}`,
        bestMatch.patient_data?.phone && `Phone: ${bestMatch.patient_data.phone}`,
        bestMatch.patient_data?.email && `Email: ${bestMatch.patient_data.email}`,
      ].filter(Boolean).join(', ');

      const confirmationMessage = (
        <Box>
          <Typography variant="body1" gutterBottom>
            You&apos;re about to create a new patient record, but we found a very likely match:
          </Typography>

          <Box sx={{ my: 2, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
            <Typography variant="subtitle2" color="error.dark" sx={{ fontWeight: "bold" }}>
              📋 {patientName} ({bestMatch.confidence}% confidence match)
            </Typography>
            {patientDetails && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {patientDetails}
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            This could create a duplicate patient record. Are you sure you want to continue?
          </Typography>
        </Box>
      );

      const confirmed = await confirm({
        title: '⚠️ High Confidence Match Detected',
        message: confirmationMessage,
        confirmText: 'Continue - Import as New',
        cancelText: 'Cancel',
        severity: 'warning'
      });

      if (!confirmed) {
        return; // User cancelled
      }
    }

    // Proceed with import
    onMergeDecision?.(patient.extracted_patient_id, 'import_as_new');
  };

  const canMerge = Boolean(targetPatientId && (mergeExtracted || mergeExistingIds.size > 0));
  const mergeCount = (mergeExtracted ? 1 : 0) + mergeExistingIds.size;

  // Calculate target and source names for the summary
  const targetMatch = matches.find(m => m.patient_id === targetPatientId);
  const targetPatientName = targetMatch ?
    `${targetMatch.patient_data?.first_name || ''} ${targetMatch.patient_data?.last_name || ''}`.trim() : '';

  const sourceNames: string[] = [];
  if (mergeExtracted) {
    const extractedName = `${patient.imported_patient_data?.first_name || ''} ${patient.imported_patient_data?.last_name || ''}`.trim();
    sourceNames.push(extractedName + ' (Extracted)');
  }
  mergeExistingIds.forEach(id => {
    const match = matches.find(m => m.patient_id === id);
    if (match) {
      const name = `${match.patient_data?.first_name || ''} ${match.patient_data?.last_name || ''}`.trim();
      sourceNames.push(name + ' (Existing)');
    }
  });

  // Show simple view for patients without matches
  if (!hasPatientMatches) {
    return (
      <>
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            border: isSelected ? '2px solid' : '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            opacity: isProcessed ? 0.7 : 1,
            bgcolor: isProcessed ? 'action.disabledBackground' : 'background.paper',
          }}
        >
          <CardContent>
            {/* Simple header for no matches */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {onToggleSelection && (
                  <Checkbox
                    checked={isSelected}
                    disabled={isProcessed}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelection(patient.extracted_patient_id);
                    }}
                    size="small"
                  />
                )}
                <PersonIcon color="primary" />
                <Typography variant="h6" component="div">
                  {patient.imported_patient_data?.first_name} {patient.imported_patient_data?.last_name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="View JSON data">
                  <IconButton
                    size="small"
                    onClick={() => setJsonModalOpen(true)}
                    sx={{ color: 'primary.main' }}
                  >
                    <DataObjectIcon />
                  </IconButton>
                </Tooltip>
                {isProcessed && (
                  <Chip
                    label="Already Processed"
                    color="success"
                    size="small"
                  />
                )}
                <Chip
                  label={patient.import_status}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>

            <Alert severity="success" variant="outlined">
              <Typography variant="body2">
                <strong>No duplicates found</strong> - Safe to import as new patient.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <ConfirmationDialog />

        {/* JSON Viewer Modal */}
        <PatientDataJsonModal
          open={jsonModalOpen}
          onClose={() => setJsonModalOpen(false)}
          patient={patient}
        />
      </>
    );
  }

  // Handle edge case: patient has match status but no actual match data
  if (matches.length === 0 && patient.import_status === 'matched') {
    return (
      <>
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            border: isSelected ? '2px solid' : '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            opacity: isProcessed ? 0.7 : 1,
            bgcolor: isProcessed ? 'action.disabledBackground' : 'background.paper',
          }}
        >
          <CardContent>
            {/* Header with selection checkbox and status */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {onToggleSelection && (
                  <Checkbox
                    checked={isSelected}
                    disabled={isProcessed}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelection(patient.extracted_patient_id);
                    }}
                    size="small"
                  />
                )}
                <PersonIcon color="primary" />
                <Typography variant="h6" component="div">
                  {patient.imported_patient_data?.first_name} {patient.imported_patient_data?.last_name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="View JSON data">
                  <IconButton
                    size="small"
                    onClick={() => setJsonModalOpen(true)}
                    sx={{ color: 'primary.main' }}
                  >
                    <DataObjectIcon />
                  </IconButton>
                </Tooltip>
                {isProcessed && (
                  <Chip
                    label="Already Processed"
                    color="success"
                    size="small"
                  />
                )}
                <Chip
                  label={patient.import_status}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>

            {/* Extracted Patient Section */}
            <ExtractedPatientSection patient={patient} />

            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Match status detected but no match data available.</strong>
                You can import this patient as new or contact support if this appears to be an error.
              </Typography>
            </Alert>

            {/* Simple action for import as new */}
            <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<PersonAddIcon />}
                onClick={handleImportAsNew}
                disabled={isLoading || isProcessed}
                size="large"
              >
                Import as New Patient
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <ConfirmationDialog />

        {/* JSON Viewer Modal */}
        <PatientDataJsonModal
          open={jsonModalOpen}
          onClose={() => setJsonModalOpen(false)}
          patient={patient}
        />
      </>
    );
  }

  // Enhanced view for patients with matches
  return (
    <>
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        opacity: isProcessed ? 0.7 : 1,
        bgcolor: isProcessed ? 'action.disabledBackground' : 'background.paper',
      }}
    >
      <CardContent>
        {/* Header with selection checkbox and status */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onToggleSelection && (
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelection(patient.extracted_patient_id);
                }}
                size="small"
              />
            )}
            <PersonIcon color="primary" />
            <Typography variant="h6" component="div">
              Multi-Patient Merge Required
            </Typography>
            <Chip
              label={`${matches.length} potential match${matches.length !== 1 ? 'es' : ''}`}
              color="warning"
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="View JSON data">
              <IconButton
                size="small"
                onClick={() => setJsonModalOpen(true)}
                sx={{ color: 'primary.main' }}
              >
                <DataObjectIcon />
              </IconButton>
            </Tooltip>
            <Chip
              label={patient.import_status}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>

        {/* Extracted Patient Section */}
        <ExtractedPatientSection patient={patient} />

        {/* Target Selection Section */}
        <TargetSelectionSection
          matches={matches}
          selectedTarget={targetPatientId}
          onTargetChange={setTargetPatientId}
        />

        {/* Source Selection Section */}
        <SourceSelectionSection
          extractedPatient={patient}
          potentialMatches={matches}
          targetPatientId={targetPatientId}
          mergeExtracted={mergeExtracted}
          mergeExistingIds={mergeExistingIds}
          onMergeExtractedChange={isProcessed ? () => {} : setMergeExtracted}
          onMergeExistingChange={isProcessed ? () => {} : setMergeExistingIds}
        />

        {/* Action Buttons */}
        <ActionButtonsSection
          canMerge={canMerge && !isProcessed}
          mergeCount={mergeCount}
          onMerge={handleMerge}
          onImportNew={handleImportAsNew}
          isLoading={isLoading || isProcessed}
          targetPatientName={targetPatientName}
          sourceNames={sourceNames}
        />
      </CardContent>
    </Card>

    {/* Confirmation Dialog */}
    <ConfirmationDialog />

    {/* JSON Viewer Modal */}
    <PatientDataJsonModal
      open={jsonModalOpen}
      onClose={() => setJsonModalOpen(false)}
      patient={patient}
    />
    </>
  );
};

export default PatientMatchReview;